import React from 'react';
import { useAds } from '../../context/AdContext';

function AdLabel() {
  return (
    <span
      className="absolute top-2 right-2 text-[9px] font-medium px-1.5 py-0.5 rounded"
      style={{ background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.5)' }}
    >
      Reklam
    </span>
  );
}

/* Yan sütun için dikey kart */
function VerticalAd({ ad }) {
  const content = (
    <div
      className="rounded-2xl overflow-hidden relative"
      style={{
        background: ad.bgColor || '#f5f5f7',
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        minHeight: 120,
      }}
    >
      {ad.mediaUrl && (
        <img
          src={ad.mediaUrl}
          alt={ad.title || 'Reklam'}
          className="w-full object-cover"
          style={{ maxHeight: 180 }}
        />
      )}
      <div className="p-3">
        {ad.title && (
          <p className="text-xs font-semibold text-apple-gray-dark leading-tight mb-1">{ad.title}</p>
        )}
        {ad.description && (
          <p className="text-xs text-apple-gray-mid leading-relaxed">{ad.description}</p>
        )}
        {ad.ctaText && (
          <span
            className="inline-block mt-2 text-xs font-medium px-3 py-1 rounded-full"
            style={{ background: ad.ctaColor || '#0a84ff', color: '#fff' }}
          >
            {ad.ctaText}
          </span>
        )}
      </div>
      <AdLabel />
    </div>
  );

  return ad.linkUrl ? (
    <a href={ad.linkUrl} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition-opacity">
      {content}
    </a>
  ) : content;
}

/* Alt bant için yatay kart */
function HorizontalAd({ ad }) {
  const content = (
    <div
      className="rounded-2xl overflow-hidden relative flex items-center gap-4"
      style={{
        background: ad.bgColor || '#f5f5f7',
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        minHeight: 90,
      }}
    >
      {ad.mediaUrl && (
        <img
          src={ad.mediaUrl}
          alt={ad.title || 'Reklam'}
          className="h-full object-cover shrink-0"
          style={{ width: 140, minHeight: 90, maxHeight: 120 }}
        />
      )}
      <div className="flex-1 py-3 pr-4" style={{ paddingLeft: ad.mediaUrl ? 0 : '1rem' }}>
        {ad.title && (
          <p className="text-sm font-semibold text-apple-gray-dark mb-1">{ad.title}</p>
        )}
        {ad.description && (
          <p className="text-xs text-apple-gray-mid leading-relaxed">{ad.description}</p>
        )}
        {ad.ctaText && (
          <span
            className="inline-block mt-2 text-xs font-medium px-4 py-1.5 rounded-full"
            style={{ background: ad.ctaColor || '#0a84ff', color: '#fff' }}
          >
            {ad.ctaText}
          </span>
        )}
      </div>
      <AdLabel />
    </div>
  );

  return ad.linkUrl ? (
    <a href={ad.linkUrl} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition-opacity">
      {content}
    </a>
  ) : content;
}

/* Yan kolonu tamamen kaplayan tam boy reklam */
function FullSideAd({ ad }) {
  const content = (
    <div
      className="relative overflow-hidden flex-1"
      style={{
        background: ad.bgColor || '#f5f5f7',
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        borderRadius: '1rem',
        minHeight: 300,
      }}
    >
      {ad.mediaUrl && (
        <img
          src={ad.mediaUrl}
          alt={ad.title || 'Reklam'}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {/* Gradient overlay — metin okunabilirliği */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)' }}
      />
      <div className="absolute bottom-0 left-0 right-0 p-3">
        {ad.title && (
          <p className="text-xs font-semibold text-white leading-tight mb-1">{ad.title}</p>
        )}
        {ad.description && (
          <p className="text-[10px] text-white/80 leading-relaxed">{ad.description}</p>
        )}
        {ad.ctaText && (
          <span
            className="inline-block mt-2 text-xs font-medium px-3 py-1 rounded-full"
            style={{ background: ad.ctaColor || '#0a84ff', color: '#fff' }}
          >
            {ad.ctaText}
          </span>
        )}
      </div>
      <AdLabel />
    </div>
  );

  return ad.linkUrl ? (
    <a href={ad.linkUrl} target="_blank" rel="noopener noreferrer" className="flex flex-1 hover:opacity-90 transition-opacity">
      {content}
    </a>
  ) : content;
}

/* position: 'left' | 'right'           → dikey kart  */
/* position: 'left-full' | 'right-full' → tam boy yan */
/* position: 'bottom'                   → yatay bant  */
export default function AdBanner({ position }) {
  const { getActiveAds } = useAds();
  const ads = getActiveAds(position);

  if (ads.length === 0) return null;

  if (position === 'top' || position === 'bottom') {
    return (
      <div className="flex flex-col gap-3 w-full">
        {ads.map(ad => <HorizontalAd key={ad.id} ad={ad} />)}
      </div>
    );
  }

  if (position === 'left-full' || position === 'right-full') {
    return (
      <div className="flex flex-col gap-3 flex-1 mt-3">
        {ads.map(ad => <FullSideAd key={ad.id} ad={ad} />)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {ads.map(ad => <VerticalAd key={ad.id} ad={ad} />)}
    </div>
  );
}
